import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import axios from 'axios';
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import base from '../../config';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import InfoIcon from '@material-ui/icons/Info';
import { toast } from 'react-toastify';
import CloudIcon from '@material-ui/icons/Cloud';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import AppsIcon from '@material-ui/icons/Apps';
import PersonIcon from '@material-ui/icons/Person';

/**
 * Component for DedicatedVPC Approvals
 * @author shilpa.adikeshava
 * @version 1.0
 */

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    background: 'white',
    borderRadius: '6px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e9ecef',
    overflow: 'hidden',
  },
  tableHead: {
    background: 'linear-gradient(135deg, #7500C0 0%, #5a0090 100%)',
    '& .MuiTableCell-head': {
      color: 'white',
      fontWeight: 600,
      fontSize: '0.875rem',
      padding: theme.spacing(2, 1.5),
      borderBottom: 'none',
    },
  },
  tableRow: {
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#f8f9fa',
      transform: 'scale(1.001)',
    },
    '& .MuiTableCell-body': {
      fontSize: '0.813rem',
      padding: theme.spacing(2, 1.5),
      borderBottom: '1px solid #e0e0e0',
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    backgroundColor: '#f8f9fa',
  },
  emptyIcon: {
    fontSize: '4rem',
    color: '#7500C0',
    opacity: 0.3,
    marginBottom: theme.spacing(2),
  },
  emptyText: {
    color: '#666',
    fontSize: '1.1rem',
    fontWeight: 500,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '300px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
  },
  loadingText: {
    marginLeft: theme.spacing(2),
    color: '#7500C0',
    fontSize: '1.1rem',
    fontWeight: 500,
  },
  actionButton: {
    margin: theme.spacing(0.5),
    minWidth: '80px',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    borderRadius: '4px',
    padding: '6px 12px',
  },
  approveButton: {
    backgroundColor: '#48bb78',
    color: 'white',
    '&:hover': {
      backgroundColor: '#38a169',
    },
  },
  rejectButton: {
    backgroundColor: '#f56565',
    color: 'white',
    '&:hover': {
      backgroundColor: '#e53e3e',
    },
  },
  statusChip: {
    padding: '4px 12px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    backgroundColor: '#fff3cd',
    color: '#856404',
    border: '1px solid #ffeaa7',
  },
  dialogPaper: {
    borderRadius: '8px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    overflow: 'hidden',
    maxWidth: '900px',
    width: '90vw',
  },
  dialogTitle: {
    background: 'linear-gradient(135deg, #7500C0 0%, #5a0090 100%)',
    color: 'white',
    padding: theme.spacing(3, 4),
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: 'rgba(255, 255, 255, 0.3)',
    },
  },
  dialogTitleText: {
    fontSize: '1.25rem',
    fontWeight: 600,
    letterSpacing: 'normal',
  },
  dialogContent: {
    padding: theme.spacing(2, 3),
    backgroundColor: '#fafbfc',
  },
  confirmText: {
    fontSize: '1rem',
    fontWeight: 400,
    color: '#4a5568',
    marginBottom: theme.spacing(2),
  },
  requestIdText: {
    color: '#2d3748',
    fontWeight: 500,
    fontSize: '0.9rem',
    marginBottom: theme.spacing(2),
  },
  commentsField: {
    backgroundColor: 'white',
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#e2e8f0',
      },
      '&:hover fieldset': {
        borderColor: '#cbd5e0',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#7500C0',
      },
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#7500C0',
    },
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    backgroundColor: '#f8f9fa',
    borderTop: '1px solid #e9ecef',
    gap: theme.spacing(1),
  },
  cancelButton: {
    color: '#4a5568',
    borderColor: '#e2e8f0',
    minWidth: '100px',
    fontWeight: 500,
    '&:hover': {
      borderColor: '#cbd5e0',
      backgroundColor: '#f7fafc',
    },
  },
  confirmButton: {
    minWidth: '100px',
    fontWeight: 600,
    textTransform: 'none',
  },
  acknowledgementBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '8px',
  },
  acknowledgementText: {
    fontSize: '0.875rem',
    color: '#856404',
    fontWeight: 500,
  },
  billingInfoBox: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#e3f2fd',
    border: '1px solid #2196f3',
    borderRadius: '8px',
  },
  billingInfoText: {
    fontSize: '0.875rem',
    color: '#1565c0',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'flex-start',
  },
  infoIcon: {
    marginRight: theme.spacing(1),
    marginTop: '2px',
    fontSize: '1rem',
  },
  // Statistics Cards Styles - Same as App/Platform Orders
  statsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(4),
    marginBottom: theme.spacing(4),
    width: '100%',
    '& > *': {
      flex: '1 1 250px',
      minWidth: '250px',
    }
  },
  statCard: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    borderRadius: '6px',
    padding: theme.spacing(3),
    boxShadow: '0 4px 16px rgba(117, 0, 192, 0.08)',
    border: '1px solid #e9ecef',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    margin: theme.spacing(1),
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 24px rgba(117, 0, 192, 0.15)',
      border: '1px solid #7500C0',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: '#7500C0',
    }
  },
  statNumber: {
    fontSize: '3.5rem',
    fontWeight: 800,
    background: 'linear-gradient(135deg, #7500C0 0%, #5a0090 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    lineHeight: 1,
    marginBottom: theme.spacing(1),
  },
  statLabel: {
    fontSize: '0.95rem',
    color: '#4a5568',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing(0.5),
  },
  statIcon: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    fontSize: '2rem',
    opacity: 0.1,
    color: '#7500C0',
  },
  statSubtext: {
    fontSize: '0.875rem',
    color: '#718096',
    fontWeight: 500,
  },
  // Filters Styles - Same as App/Platform Orders
  filtersContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    border: '1px solid #e0e0e0',
  },
  filtersHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: '16px',
    color: '#7500C0',
    fontWeight: 'bold',
    fontSize: '1.1rem',
  },
  filtersRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '& > *': {
      flex: '1 1 auto',
      minWidth: '140px',
    },
  },
  filterControl: {
    flex: '1 1 auto',
    minWidth: '140px',
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: '#7500C0',
      },
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#7500C0',
    },
  },
  searchField: {
    flex: '2 1 auto',
    minWidth: '200px',
  },
  clearFiltersBtn: {
    color: '#666',
    borderColor: '#ddd',
    flex: '0 0 auto',
    minWidth: '100px',
  },
}));

const DedicatedVpcApprovals = ({
  email,
  history,
  onCountChange,
  onDataChange,
  countOnly = false,
  initialData = [],
  skipInitialFetch = false
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [approvals, setApprovals] = useState(initialData);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionType, setActionType] = useState('');
  const [comments, setComments] = useState('');
  const [processing, setProcessing] = useState(false);
  const [acknowledgementChecked, setAcknowledgementChecked] = useState(false);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Filter states for VPC approvals
  const [filters, setFilters] = useState({
    search: '',
    cloudPlatform: 'all',
    region: 'all',
    status: 'all',
  });

  // Fetch DedicatedVPC approvals
  const fetchApprovals = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const idToken = localStorage.getItem("token");
      const response = await axios.get(
        `${base.app.apiSite}provision/mywizard-aiops/read/dedicatedvpcrequests`,
        {
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
            "Authorization": idToken,
          },
          params: {
            email: email,
            status: "Pending L1 Approval" // Only fetch pending approvals
          }
        }
      );

      if (response.data) {
        // Handle different possible response formats
        let data = [];
        if (Array.isArray(response.data)) {
          data = response.data;
        } else if (response.data.statusCode === 200 && response.data.body && response.data.body.data) {
          data = response.data.body.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          data = response.data.data;
        }

        // Filter for pending approvals assigned to current user
        const pendingApprovals = data.filter(item =>
          (item.ApprovalStatus === "Pending L1 Approval" || item.ApprovalStatus === "Pending Approval") &&
          item.Approver === email
        );

        setApprovals(pendingApprovals);
        // Share data with parent for other instances
        if (onDataChange) {
          onDataChange(pendingApprovals);
        }
      }
    } catch (error) {
      console.error('Error fetching DedicatedVPC approvals:', error);
      toast.error('Failed to fetch DedicatedVPC approvals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (email && !skipInitialFetch) {
      fetchApprovals();
      // Refresh every 10 seconds (without loading indicator)
      const interval = setInterval(() => fetchApprovals(false), 10000);
      return () => clearInterval(interval);
    } else if (skipInitialFetch && initialData.length > 0) {
      // Use provided data instead of fetching
      setLoading(false);
    }
  }, [email, skipInitialFetch]);

  // Update count for VPC orders
  useEffect(() => {
    if (onCountChange) {
      const count = filteredApprovals ? filteredApprovals.length : 0;
      onCountChange(count);
    }
  }, [filteredApprovals, approvals, onCountChange]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }) + ' ' + date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  // Handle approval actions
  const handleAction = async () => {
    if (!acknowledgementChecked) {
      toast.warning('Please acknowledge your responsibility before proceeding');
      return;
    }

    setProcessing(true);
    try {
      const idToken = localStorage.getItem("token");
      const endpoint = actionType === 'approve' ? 'approved' : 'declined';
      const approverField = actionType === 'approve' ? 'ApprovedBy' : 'DeclinedBy';

      const params = new URLSearchParams({
        OrderItemUId: selectedApproval.OrderItemUId,
        ApprovalStatus: actionType === 'approve' ? 'Approved' : 'Declined',
        Comments: comments || '',
        [approverField]: email
      });

      await axios.post(
        `${base.app.apiSite}provision/mywizard-aiops/dedicated-vpc-deployment-request/${endpoint}?${params.toString()}`,
        null,
        {
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
            "Authorization": idToken,
          }
        }
      );

      toast.success(`Dedicated VPC request ${actionType === 'approve' ? 'approved' : 'rejected'} successfully`);
      setShowActionDialog(false);
      setComments('');
      setSelectedApproval(null);
      setAcknowledgementChecked(false);
      fetchApprovals(false); // Refresh the list without loading indicator
    } catch (error) {
      console.error(`Error ${actionType}ing request:`, error);
      toast.error('Failed to ' + actionType + ' request');
    } finally {
      setProcessing(false);
    }
  };

  const openActionDialog = (approval, type) => {
    setSelectedApproval(approval);
    setActionType(type);
    setShowActionDialog(true);
    setAcknowledgementChecked(false);
    setComments('');
  };

  const closeActionDialog = () => {
    setShowActionDialog(false);
    setComments('');
    setSelectedApproval(null);
    setAcknowledgementChecked(false);
  };

  // Filter functions
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setPage(0); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      cloudPlatform: 'all',
      region: 'all',
      status: 'all',
    });
    setPage(0); // Reset to first page when clearing filters
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Apply filters to approvals using useMemo for proper dependency tracking
  const filteredApprovals = useMemo(() => approvals.filter(approval => {
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matches =
        (approval.OrderItemUId || '').toLowerCase().includes(searchLower) ||
        (approval.CloudPlatform || '').toLowerCase().includes(searchLower) ||
        (approval.DeploymentRegion || '').toLowerCase().includes(searchLower) ||
        (approval.AppList || '').toLowerCase().includes(searchLower) ||
        (approval.CustomerEnterpriseID || '').toLowerCase().includes(searchLower);
      if (!matches) return false;
    }

    // Cloud Platform filter
    if (filters.cloudPlatform !== 'all') {
      if ((approval.CloudPlatform || '').toLowerCase() !== filters.cloudPlatform.toLowerCase()) {
        return false;
      }
    }

    // Region filter
    if (filters.region !== 'all') {
      if ((approval.DeploymentRegion || '').toLowerCase() !== filters.region.toLowerCase()) {
        return false;
      }
    }

    // Status filter
    if (filters.status !== 'all') {
      if ((approval.ApprovalStatus || '').toLowerCase() !== filters.status.toLowerCase()) {
        return false;
      }
    }

    return true;
  }), [approvals, filters]);

  // Paginated data for table display
  const paginatedApprovals = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredApprovals.slice(startIndex, endIndex);
  }, [filteredApprovals, page, rowsPerPage]);

  // If this is count-only mode, don't render UI
  if (countOnly) {
    return null;
  }

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={40} style={{ color: '#7500C0' }} />
        <Typography className={classes.loadingText}>
          Loading Dedicated VPC approvals...
        </Typography>
      </div>
    );
  }

  return (
    <>
      {/* Statistics Cards for VPC Approvals */}
      <div className={classes.statsContainer}>
        <div className={classes.statCard}>
          <CloudIcon className={classes.statIcon} />
          <Typography className={classes.statNumber}>
            {approvals.length}
          </Typography>
          <Typography className={classes.statLabel}>
            Total VPC Requests
          </Typography>
          <Typography className={classes.statSubtext}>
            Pending your approval
          </Typography>
        </div>
        <div className={classes.statCard}>
          <LocationOnIcon className={classes.statIcon} />
          <Typography className={classes.statNumber}>
            {[...new Set(approvals.map(a => a.DeploymentRegion))].filter(Boolean).length}
          </Typography>
          <Typography className={classes.statLabel}>
            Regions
          </Typography>
          <Typography className={classes.statSubtext}>
            Deployment regions
          </Typography>
        </div>
        <div className={classes.statCard}>
          <AppsIcon className={classes.statIcon} />
          <Typography className={classes.statNumber}>
            {[...new Set(approvals.map(a => a.CloudPlatform))].filter(Boolean).length}
          </Typography>
          <Typography className={classes.statLabel}>
            Platforms
          </Typography>
          <Typography className={classes.statSubtext}>
            Cloud platforms
          </Typography>
        </div>
        <div className={classes.statCard}>
          <PersonIcon className={classes.statIcon} />
          <Typography className={classes.statNumber}>
            {[...new Set(approvals.map(a => a.CustomerEnterpriseID))].filter(Boolean).length}
          </Typography>
          <Typography className={classes.statLabel}>
            Requestors
          </Typography>
          <Typography className={classes.statSubtext}>
            Unique requestors
          </Typography>
        </div>
      </div>

      {/* Filters Section for VPC Approvals */}
      <div className={classes.filtersContainer}>
        <div className={classes.filtersHeader}>
          <Typography component="span" style={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
            Filter VPC Requests
          </Typography>
          <Typography variant="body2" style={{ color: '#666', fontWeight: 500, marginLeft: 'auto' }}>
            {filteredApprovals.length} of {approvals.length} requests
          </Typography>
        </div>

        <div className={classes.filtersRow}>
          {/* Search Filter */}
          <TextField
            className={`${classes.filterControl} ${classes.searchField}`}
            label="Search"
            variant="outlined"
            size="small"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            placeholder="Search by Request ID, Platform, Region..."
          />

          {/* Cloud Platform Filter */}
          <FormControl variant="outlined" size="small" className={classes.filterControl}>
            <InputLabel>Cloud Platform</InputLabel>
            <Select
              value={filters.cloudPlatform}
              onChange={(e) => handleFilterChange('cloudPlatform', e.target.value)}
              label="Cloud Platform"
            >
              <MenuItem value="all">All Platforms</MenuItem>
              <MenuItem value="aws">AWS</MenuItem>
              <MenuItem value="azure">Azure</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          {/* Region Filter */}
          <FormControl variant="outlined" size="small" className={classes.filterControl}>
            <InputLabel>Region</InputLabel>
            <Select
              value={filters.region}
              onChange={(e) => handleFilterChange('region', e.target.value)}
              label="Region"
            >
              <MenuItem value="all">All Regions</MenuItem>
              <MenuItem value="virginia">Virginia</MenuItem>
            </Select>
          </FormControl>

          {/* Status Filter */}
          <FormControl variant="outlined" size="small" className={classes.filterControl}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="pending l1 approval">Pending</MenuItem>
            </Select>
          </FormControl>

          {/* Clear Filters Button */}
          <Button
            variant="outlined"
            size="small"
            onClick={clearFilters}
            className={classes.clearFiltersBtn}
          >
            Clear Filters
          </Button>
        </div>
      </div>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell>Request ID</TableCell>
              <TableCell>Cloud Platform</TableCell>
              <TableCell>Deployment Region</TableCell>
              <TableCell>Applications</TableCell>
              <TableCell>Requestor</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredApprovals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className={classes.emptyState}>
                  <CheckCircleIcon className={classes.emptyIcon} />
                  <Typography className={classes.emptyText}>
                    No pending Dedicated VPC approvals at this time
                  </Typography>
                  <Typography variant="body2" style={{ color: '#999', marginTop: 8 }}>
                    Approved requests will appear here for your review
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedApprovals.map((approval, index) => (
                <TableRow key={index} className={classes.tableRow}>
                  <TableCell>
                    <Typography variant="body2" style={{ fontFamily: 'Monaco, monospace', fontWeight: 600, color: '#4a5568' }}>
                      {approval.OrderItemUId || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 500, color: '#2d3748' }}>
                      {approval.CloudPlatform || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 500, color: '#2d3748' }}>
                      {approval.DeploymentRegion || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 500, color: '#2d3748' }}>
                      {approval.AppList || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 500, color: '#2d3748' }}>
                      {approval.CustomerEnterpriseID || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontSize: '0.75rem', color: '#666' }}>
                      {formatDate(approval.CreatedDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <span className={classes.statusChip}>
                      Pending
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      className={`${classes.actionButton} ${classes.approveButton}`}
                      size="small"
                      onClick={() => openActionDialog(approval, 'approve')}
                      startIcon={<CheckCircleIcon />}
                    >
                      Approve
                    </Button>
                    <Button
                      className={`${classes.actionButton} ${classes.rejectButton}`}
                      size="small"
                      onClick={() => openActionDialog(approval, 'reject')}
                      startIcon={<CancelIcon />}
                    >
                      Decline
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredApprovals.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        style={{
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#fafbfc'
        }}
      />

      {/* Action Dialog */}
      <Dialog
        open={showActionDialog}
        onClose={closeActionDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: classes.dialogPaper
        }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Typography className={classes.dialogTitleText}>
            {actionType === 'approve' ? 'Approve Request' : 'Decline Request'}
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Typography className={classes.confirmText}>
            Are you sure you want to {actionType} this Dedicated VPC request?
          </Typography>
          <Typography className={classes.requestIdText}>
            Request ID: {selectedApproval && selectedApproval.OrderItemUId}
          </Typography>

          {actionType === 'approve' && (
            <Box className={classes.billingInfoBox}>
              <Typography className={classes.billingInfoText}>
                <InfoIcon className={classes.infoIcon} />
                <span>
                  <strong>Important:</strong> Monthly recovery for Infrastructure provisioned for this order will be billed to the requester.
                </span>
              </Typography>
            </Box>
          )}

          <TextField
            multiline
            rows={4}
            fullWidth
            label={`${actionType === 'approve' ? 'Approval' : 'Decline'} Comments`}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            margin="normal"
            variant="outlined"
            className={classes.commentsField}
            placeholder={`Add ${actionType === 'approve' ? 'approval notes and next steps...' : 'reason for decline...'}`}
          />

          <Box className={classes.acknowledgementBox}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledgementChecked}
                  onChange={(e) => setAcknowledgementChecked(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography className={classes.acknowledgementText}>
                  I acknowledge my responsibility for this approval decision and confirm that I have reviewed the request details thoroughly.
                </Typography>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={closeActionDialog}
            disabled={processing}
            variant="outlined"
            className={classes.cancelButton}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAction}
            disabled={processing || !acknowledgementChecked}
            variant="contained"
            style={{
              backgroundColor: actionType === 'approve' ? '#48bb78' : '#f56565',
              color: 'white'
            }}
            className={classes.confirmButton}
          >
            {processing ? (
              <CircularProgress size={20} style={{ color: 'white' }} />
            ) : (
              actionType === 'approve' ? 'Approve Request' : 'Decline Request'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

DedicatedVpcApprovals.propTypes = {
  email: PropTypes.string,
  history: PropTypes.object,
  onCountChange: PropTypes.func,
  onDataChange: PropTypes.func,
  countOnly: PropTypes.bool,
  initialData: PropTypes.array,
  skipInitialFetch: PropTypes.bool,
};

const MSTP = (state) => ({
  email: state.auth.userDetails.email,
});

export default connect(MSTP)(withRouter(DedicatedVpcApprovals));